import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { useApi2 } from "@/services/api2";

export type HelpCenterQuery = {
    key?: string;
    query: string;
    heading?: string;
    loading: boolean;
    catalogs: number[];
}

export type ResultSpan = {
    start: number;
    end: number;
    source: number;
}

export type ResultSource = {
    documentName: string;
    title: string;
    excerpt: string;
    url?: string;
    icon: string;
}

export type Result = {
    text: string;
    sources: ResultSource[]
    spans: ResultSpan[];
}

export const useHelpCenterStore = defineStore("help-center", () => {
    const isShowing = ref(false);
    const hasNewItems = ref(false);
    const items = ref<HelpCenterQuery[]>([]);
    const selected = ref<HelpCenterQuery | null>(null);
    const loading = ref(false);
    const result = ref<Result | null>(null);

    const { generateHeading, helpCenterSearch } = useApi2();

    function load() {
        if (!selected.value) { return; }
        loading.value = true;
        helpCenterSearch(selected.value?.query!, selected.value.catalogs, (data) => {
            result.value = data;
            loading.value = false;
        });
    }

    watch(selected, load);

    function addItem(query: HelpCenterQuery) {
        items.value.push(query);
        if (!isShowing.value) {
            hasNewItems.value = true;
        }
        return items.value[items.value.length - 1];
    }

    async function queueCandidate(query: string, catalogs: number[], key?: string) {
        const item = addItem({ query, key, loading: true, catalogs });
        const heading = await generateHeading(query);
        item.heading = heading;
        item.loading = false;
    }
    async function queryNow(query: string, catalogs: number[], key?: string) {
        const item = addItem({ query, key, loading: false, catalogs });
        selected.value = item;
    }

    watch(isShowing, () => {
        hasNewItems.value = false;
    });

    return {
        items,
        selected,
        isShowing,
        addItem,
        hasNewItems,
        queueCandidate,
        queryNow,
        loading,
        result,
    };
});
